import { Ray } from "@babylonjs/core/Culling/ray";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Plane } from "@babylonjs/core/Maths/math.plane";
import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import "@babylonjs/core/Rendering/boundingBoxRenderer"; //side effect for Mesh.showBoundingBox
import { PbrMaterial } from "../../core/mdl";
import { PropertyChangedResult } from "../../core/prop";
import { AbstractTool, InteractionMode, LengthFeedback, SnapGrid } from "../../core/tool/";
import { setLocalMatrix } from "../../core/utils";
import { intersectRayPlane, VECTOR3_X, VECTOR3_Y, VECTOR3_Z } from "../../core/utils/MathUtils";
import { CoordinatesType } from "../../core/view";
import * as basket from "../../eaiws/basket";
import { Log } from "../../utils";
import { EPSILON, eq, PI_DIV2, roundTo } from "../../utils/math";
import { DimChangeMode } from "./DimChangeMode";
export class DimChangeTool extends AbstractTool {
    constructor(pApp, pDimChangeModeContext, pBBOrigin, pRefPoint, pDimChange1, pDimChange2, pPlane) {
        super(pApp);
        this.onCameraChanged = () => {
            //update length feedback
            for (const tFeedback of this.mLengthFeedback) {
                tFeedback.update(null, null);
            }
            this.mApp.viewer.requestRenderFrame();
        };
        this.mView = this.mApp.viewer.view;
        this.mArticle = pDimChangeModeContext.article;
        this.mRefPlane = pPlane;
        this.mBBOrigin = pBBOrigin;
        this.mDimChange1 = pDimChange1;
        this.mDimChange2 = pDimChange2;
        this.mDimChangeModeContext = pDimChangeModeContext;
        this.mPickPlane = new Plane(0, 1, 0, 0);
        this.mRefPoint = pRefPoint;
        this.mLocalStartPoint = Vector3.Zero();
        this.mScaleDirection = Vector3.Zero();
        this.mSnapGrip = new SnapGrid();
        this.mAxis = { x: null, y: null, z: null };
        this.mFeedbackNode = null;
        this.mFeedbackBox = null;
        this.mFeedbackMat = null;
        this.mLengthFeedback = [];
    }
    start(pParameter) {
        this.prepareDimChange();
        this.createFeedback();
        this.setInteractionMode(InteractionMode.Primary);
        this.mView.cameraControl.eventCameraChanged.addListener(this.onCameraChanged);
        return (super.start(pParameter));
    }
    stop() {
        this.mView.cameraControl.eventCameraChanged.removeListener(this.onCameraChanged);
        if (this.mFeedbackNode != null) {
            this.mFeedbackNode.dispose(false);
            this.mFeedbackNode = null;
            this.mFeedbackBox = null;
        }
        if (this.mFeedbackMat != null) {
            this.mFeedbackMat.dispose();
            this.mFeedbackMat = null;
        }
        for (const tFeedback of this.mLengthFeedback) {
            tFeedback.dispose();
        }
        this.mLengthFeedback = [];
        this.mApp.viewer.requestRenderFrame();
        super.stop();
    }
    handlePointerUp(pEvent, pState) {
        if (!pState.isPrimaryPointer(pEvent))
            return;
        void this.finishDimChange();
    }
    handlePointerMove(pEvent, pState) {
        if (!pState.isPrimaryPointer(pEvent))
            return;
        const tPointer = pState.pointers.get(pEvent.pointerId);
        if (tPointer == null)
            return;
        const tPointerPos = tPointer.position;
        const tCurrentPos = this.getPlanePickPos(tPointerPos);
        if (tCurrentPos == null)
            return;
        const tLocalPos = Vector3.TransformCoordinates(tCurrentPos, this.mArticle.worldTransformInverted);
        let tSize = tLocalPos.subtract(this.mLocalStartPoint);
        tSize.multiplyInPlace(this.mScaleDirection);
        tSize = this.performSnap(tSize, pEvent, pState);
        //apply scale to feedback
        if (this.mFeedbackBox != null) {
            const tPosOffset = new Vector3(0, 0, 0);
            if (tSize.x > 0 && this.mAxis.x != null) {
                this.mFeedbackBox.scaling.x = tSize.x;
                tPosOffset.x = tSize.x * this.mScaleDirection.x;
            }
            if (tSize.y > 0 && this.mAxis.y != null) {
                this.mFeedbackBox.scaling.y = tSize.y;
                tPosOffset.y = tSize.y * this.mScaleDirection.y;
            }
            if (tSize.z > 0 && this.mAxis.z != null) {
                this.mFeedbackBox.scaling.z = tSize.z;
                tPosOffset.z = tSize.z * this.mScaleDirection.z;
            }
            const tCurrentPos = this.mLocalStartPoint.add(tPosOffset);
            //update length feedback
            for (const tFeedback of this.mLengthFeedback) {
                tFeedback.update(tPointer, tCurrentPos);
            }
            this.mApp.viewer.requestRenderFrame();
        }
    }
    prepareDimChange() {
        this.mArticleBounds = this.mArticle.boundingBox.clone();
        this.mLocalStartPoint.copyFrom(this.mArticleBounds.min);
        this.mAxis = { x: null, y: null, z: null };
        this.prepareAxisData(this.mDimChange1);
        this.prepareAxisData(this.mDimChange2);
        this.preparePickPlane();
        this.prepareSnapping();
        this.detectNegativeScale();
    }
    prepareAxisData(pDimChange) {
        if (pDimChange == null)
            return;
        let tAxisV = VECTOR3_X;
        const tAxis = DimChangeMode.GetAxis(pDimChange);
        if (tAxis === "y")
            tAxisV = VECTOR3_Y;
        else if (tAxis === "z")
            tAxisV = VECTOR3_Z;
        let tChoiceValues;
        if (pDimChange.propChoiceList != null && pDimChange.propChoiceList.length > 0) {
            tChoiceValues = [];
            for (const tValue of pDimChange.propChoiceList) {
                tChoiceValues.push(tValue * pDimChange.multiplier);
            }
        }
        this.mAxis[tAxis] = {
            axis: tAxis,
            axisVec: tAxisV,
            minScale: pDimChange.minPropValue * pDimChange.multiplier,
            maxScale: pDimChange.maxPropValue * pDimChange.multiplier,
            choiceValues: tChoiceValues,
            dimChange: pDimChange
        };
    }
    preparePickPlane() {
        const tActiveAxis = new Array();
        const tWordlMatrix = this.mArticle.worldTransform;
        const tWordCenter = Vector3.TransformCoordinates(this.mArticleBounds.getCenter(), tWordlMatrix);
        const tCamDir = DimChangeMode.GetDirectionToCamera(tWordCenter, this.mView);
        for (const tAxisIndex in this.mAxis) {
            const tData = this.mAxis[tAxisIndex];
            if (tData == null)
                continue;
            tActiveAxis.push(tData);
        }
        //build pick plane
        let tPlaneNormal = tCamDir;
        if (tActiveAxis.length > 1) {
            const tWorldAxis1 = Vector3.TransformNormal(tActiveAxis[0].axisVec, tWordlMatrix);
            const tWorldAxis2 = Vector3.TransformNormal(tActiveAxis[1].axisVec, tWordlMatrix);
            tPlaneNormal = Vector3.Cross(tWorldAxis1, tWorldAxis2);
        }
        else {
            let tNormal1;
            let tNormal2;
            switch (tActiveAxis[0].axis) {
                case "y":
                    tNormal1 = Vector3.TransformNormal(VECTOR3_X, tWordlMatrix);
                    tNormal2 = Vector3.TransformNormal(VECTOR3_Z, tWordlMatrix);
                    break;
                case "z":
                    tNormal1 = Vector3.TransformNormal(VECTOR3_X, tWordlMatrix);
                    tNormal2 = Vector3.TransformNormal(VECTOR3_Y, tWordlMatrix);
                    break;
                default:
                    tNormal1 = Vector3.TransformNormal(VECTOR3_Y, tWordlMatrix);
                    tNormal2 = Vector3.TransformNormal(VECTOR3_Z, tWordlMatrix);
            }
            const tAbsDot1 = Math.abs(Vector3.Dot(tCamDir, tNormal1));
            const tAbsDot2 = Math.abs(Vector3.Dot(tCamDir, tNormal2));
            tPlaneNormal = tAbsDot1 > tAbsDot2 ? tNormal1 : tNormal2;
        }
        this.mPickPlane = Plane.FromPositionAndNormal(this.mRefPoint, tPlaneNormal);
    }
    detectNegativeScale() {
        const tCenter = this.mArticleBounds.getCenter();
        const tLocaRefPoint = Vector3.TransformCoordinates(this.mRefPoint, this.mArticle.worldTransformInverted);
        //detect negative scale direction
        this.mScaleDirection.set(1, 1, 1);
        if (tLocaRefPoint.x < tCenter.x && this.mAxis.x != null) {
            this.mLocalStartPoint.x = this.mArticleBounds.max.x;
            this.mScaleDirection.x = -1;
        }
        if (tLocaRefPoint.y < tCenter.y && this.mAxis.y != null) {
            this.mLocalStartPoint.y = this.mArticleBounds.max.y;
            this.mScaleDirection.y = -1;
        }
        if (tLocaRefPoint.z < tCenter.z && this.mAxis.z != null) {
            this.mLocalStartPoint.z = this.mArticleBounds.max.z;
            this.mScaleDirection.z = -1;
        }
    }
    prepareSnapping() {
        this.mSnapGrip.snapXAxis = false;
        this.mSnapGrip.snapYAxis = false;
        this.mSnapGrip.snapZAxis = false;
        for (const tAxis in this.mAxis) {
            const tData = this.mAxis[tAxis];
            if (tData == null)
                continue;
            //setup grid
            switch (tData.axis) {
                case "x":
                    this.mSnapGrip.snapXAxis = true;
                    this.mSnapGrip.size.x = tData.dimChange.precision;
                    break;
                case "y":
                    this.mSnapGrip.snapYAxis = true;
                    this.mSnapGrip.size.y = tData.dimChange.precision;
                    break;
                case "z":
                    this.mSnapGrip.snapZAxis = true;
                    this.mSnapGrip.size.z = tData.dimChange.precision;
                    break;
            }
        }
    }
    createFeedback() {
        this.mFeedbackMat = new PbrMaterial("dim_feedback_mat", this.mApp.model.materials);
        this.mFeedbackMat.albedoColor = Color3.FromHexString(this.mApp.theme.mainColorBright).toLinearSpace();
        this.mFeedbackMat.roughness = 1;
        this.mFeedbackMat.metallic = 0;
        this.mFeedbackMat.alpha = 0.2;
        const tSize = this.mArticleBounds.getSize();
        this.mFeedbackNode = new TransformNode("dim_feedback_node", this.mApp.model.scene);
        setLocalMatrix(this.mFeedbackNode, this.mArticle.worldTransform);
        const tFeedbackMesh = CreateBox("dim_feedback_mesh", { size: 1, updatable: false }, this.mApp.model.scene);
        tFeedbackMesh.bakeTransformIntoVertices(Matrix.Translation(0.5 * this.mScaleDirection.x, 0.5 * this.mScaleDirection.y, 0.5 * this.mScaleDirection.z));
        tFeedbackMesh.position.copyFrom(this.mLocalStartPoint);
        tFeedbackMesh.scaling.copyFrom(tSize);
        tFeedbackMesh.parent = this.mFeedbackNode;
        tFeedbackMesh.material = this.mFeedbackMat;
        tFeedbackMesh.renderingGroupId = 1;
        tFeedbackMesh.showBoundingBox = true;
        this.mFeedbackBox = tFeedbackMesh;
        const tPlane = CreatePlane("dim_feedback_plane", { size: 1, updatable: false, sideOrientation: Mesh.DOUBLESIDE });
        tPlane.material = this.mFeedbackMat;
        tPlane.renderingGroupId = 1;
        tPlane.parent = tFeedbackMesh;
        if (this.mRefPlane === "yz") {
            const tUseOppsiteSide = eq(this.mBBOrigin.x, this.mArticleBounds.max.x);
            tPlane.bakeTransformIntoVertices(Matrix.RotationY(PI_DIV2));
            tPlane.bakeTransformIntoVertices(Matrix.Translation(tUseOppsiteSide ? 1.0 : 0.0, 0.5 * this.mScaleDirection.y, 0.5 * this.mScaleDirection.z));
        }
        else if (this.mRefPlane === "xy" || (this.mRefPlane == null && this.mAxis.y != null)) {
            const tUseOppsiteSide = eq(this.mBBOrigin.z, this.mArticleBounds.min.z);
            tPlane.bakeTransformIntoVertices(Matrix.Translation(0.5 * this.mScaleDirection.x, 0.5 * this.mScaleDirection.y, tUseOppsiteSide ? 0.0 : 1.0));
        }
        else {
            //default xz
            const tUseOppsiteSide = eq(this.mBBOrigin.y, this.mArticleBounds.max.y);
            tPlane.bakeTransformIntoVertices(Matrix.RotationX(PI_DIV2));
            tPlane.bakeTransformIntoVertices(Matrix.Translation(0.5 * this.mScaleDirection.x, tUseOppsiteSide ? 1.0 : 0.0, 0.5 * this.mScaleDirection.z));
        }
        //length feedback
        this.mLengthFeedback = [];
        const tLocalePlaneNormal = Vector3.TransformNormal(this.mPickPlane.normal, this.mArticle.worldTransformInverted);
        tLocalePlaneNormal.normalize();
        let tPosIndex = 1;
        for (const tAxis in this.mAxis) {
            const tData = this.mAxis[tAxis];
            if (tData == null)
                continue;
            let tColor;
            switch (tData.axis) {
                case "x":
                    tColor = "#c00303";
                    break;
                case "y":
                    tColor = "#95c11f";
                    break;
                default: //z
                    tColor = "#0084b4";
            }
            const tFeedback = new LengthFeedback(tPosIndex, this.mBBOrigin, tData.axisVec, tColor, tLocalePlaneNormal, this.mApp.model.scene, this.mView, this.mArticle.worldTransform, true);
            tFeedback.showAbsoluteValues = true;
            this.mLengthFeedback.push(tFeedback);
            tPosIndex--;
        }
        this.mApp.viewer.requestRenderFrame();
    }
    getPlanePickPos(pPos) {
        if (pPos != null && this.mPickPlane != null) {
            this.mView.createPickingRayToRef(pPos, CoordinatesType.Client, null, gTmpRay);
            return (intersectRayPlane(gTmpRay, this.mPickPlane));
        }
        return (null);
    }
    performSnap(pScale, pEvent, pState) {
        const tScale = this.mSnapGrip.snap(pScale, this.mView);
        for (const tAxis in this.mAxis) {
            const tData = this.mAxis[tAxis];
            if (tData == null)
                continue;
            let tCurrentScale = this.getVectorAxis(tScale, tData.axis);
            //snap to scale values
            tCurrentScale = this.snapToChoiceValues(tData, tCurrentScale);
            //handle limits
            if (tCurrentScale < tData.minScale)
                tCurrentScale = tData.minScale;
            if (tCurrentScale > tData.maxScale)
                tCurrentScale = tData.maxScale;
            //assign modified scale
            this.setVectorAxis(tScale, tData.axis, tCurrentScale);
        }
        return (tScale);
    }
    getVectorAxis(pV, pAxis) {
        return pV[pAxis];
    }
    setVectorAxis(pV, pAxis, pValue) {
        pV[pAxis] = pValue;
    }
    snapToChoiceValues(pData, pScale) {
        if (pData.choiceValues == null || pData.choiceValues.length < 1)
            return (pScale);
        let tNewScale = pScale;
        let tSmallesDist = Number.MAX_VALUE;
        for (const tValue of pData.choiceValues) {
            const tDist = Math.abs(pScale - tValue);
            if (tDist < tSmallesDist) {
                tSmallesDist = tDist;
                tNewScale = tValue;
            }
        }
        //no free values allowed
        if (pData.dimChange.fixedValue) {
            return (tNewScale);
        }
        //get snap distance in view space
        const tStart = this.getVectorAxis(this.mLocalStartPoint, pData.axis);
        const tWordlMatrix = this.mArticle.worldTransform;
        const tV1 = this.mLocalStartPoint.clone();
        const tV2 = this.mLocalStartPoint.clone();
        this.setVectorAxis(tV1, pData.axis, tStart + pScale);
        this.setVectorAxis(tV2, pData.axis, tStart + tNewScale);
        const tDist = this.mView.projectedDistance(tV1, tV2, tWordlMatrix);
        return ((tDist != null && (tDist < 15)) ? tNewScale : pScale);
    }
    async finishDimChange() {
        try {
            if (this.mApp.appCallbacks.beginAsyncOperation != null)
                this.mApp.appCallbacks.beginAsyncOperation();
            await this.applyScale();
            const tRestarted = await this.restartDimChangeMode();
            if (!tRestarted) {
                this.stop();
            }
        }
        finally {
            if (this.mApp.appCallbacks.endAsyncOperation != null)
                this.mApp.appCallbacks.endAsyncOperation();
        }
    }
    async applyScale() {
        try {
            this.mApp.model.beginModification();
            this.mApp.model.beginSelectionChange();
            this.mArticle.beginChange();
            let tUpdate = false;
            for (const tAxis in this.mAxis) {
                const tData = this.mAxis[tAxis];
                if (tData == null || this.mFeedbackBox == null)
                    continue;
                let tNewScale = this.getVectorAxis(this.mFeedbackBox.scaling, tData.axis);
                //convert to unit of property and round it
                tNewScale = roundTo(tNewScale / tData.dimChange.multiplier, tData.dimChange.propDecDigits);
                //compare if scale changed and apply
                if (!eq(tNewScale, tData.dimChange.propValue)) {
                    let tNewPropValue;
                    if (tData.dimChange.symbolicPropValues != null && tData.dimChange.propChoiceList != null) {
                        //convert number to symbol -> find index of prop choice
                        const tIndex = tData.dimChange.propChoiceList.findIndex((pValue) => eq(pValue, tNewScale));
                        if (tIndex >= 0) {
                            tNewPropValue = tData.dimChange.symbolicPropValues[tIndex];
                        }
                        else {
                            Log.warn("OAP DimChage: Failed to convert numeric to symbolic property value.");
                            tNewPropValue = tData.dimChange.symbolicPropValues[0];
                        }
                    }
                    else {
                        tNewPropValue = tNewScale.toString();
                    }
                    const tResult = await this.mArticle.setEaiwsPropertyValue(tData.dimChange.propClass != null ? tData.dimChange.propClass : "", tData.dimChange.propName, tNewPropValue, false);
                    tUpdate = tUpdate || tResult !== PropertyChangedResult.Nothing;
                }
            }
            if (tUpdate) {
                await this.mArticle.getMainArticle().updateGeometry();
                this.adjustArticlePosition();
                this.mApp.viewer.requestRenderFrame();
            }
        }
        finally {
            this.mArticle.endChange();
            this.mApp.model.endSelectionChange();
            this.mApp.model.endModification();
        }
    }
    adjustArticlePosition() {
        //we can't change the position of sub articles
        const tMainArticle = this.mArticle.getMainArticle();
        if (tMainArticle !== this.mArticle)
            return;
        //get index of boundingbox origin
        let tBBOriginIndex = 0;
        const tBBPoints = this.mArticleBounds.getPoints();
        for (let i = 0; i < tBBPoints.length; ++i) {
            if (this.mBBOrigin.equalsWithEpsilon(tBBPoints[i], EPSILON)) {
                tBBOriginIndex = i;
                break;
            }
        }
        const tWordlMatrix = this.mArticle.worldTransform;
        const tOldOrigin = Vector3.TransformCoordinates(this.mArticleBounds.getPoints()[tBBOriginIndex], tWordlMatrix);
        const tNewOrigin = Vector3.TransformCoordinates(this.mArticle.boundingBox.getPoints()[tBBOriginIndex], tWordlMatrix);
        const tOffset = tOldOrigin.subtract(tNewOrigin);
        this.mArticle.node.position.addInPlace(tOffset);
        this.mArticle.updateTransformation();
    }
    async restartDimChangeMode() {
        var _a;
        //check if article is still selected and was not deleted
        if (!this.mArticle.isSelected || !this.mArticle.isInScene())
            return (false);
        const tInteractors = await this.mArticle.getMainArticle()._getOapInteractors(this.mArticle);
        if (tInteractors == null)
            return (false);
        //find updated oap interactor (with same id)
        for (const tInteractor of tInteractors) {
            if (tInteractor.id === this.mDimChangeModeContext.oapInteractorId) {
                //get dim change and start tool
                if (tInteractor.directActions != null && tInteractor.directActions.length > 0) {
                    const tAction = tInteractor.directActions[0].actionData;
                    if (tAction != null && (tAction instanceof basket.OAPDimChangeAction ||
                        tAction instanceof basket.OAPDimChange2Action)) {
                        const tTool = new DimChangeMode(this.mApp, this.mDimChangeModeContext, (_a = tAction.dimChanges) !== null && _a !== void 0 ? _a : []);
                        this.mApp.tools.startTool(tTool);
                        return (true);
                    }
                }
            }
        }
        return (false);
    }
}
//// temp variables ////////////////////////////////////////////////////////////////////////////
const gTmpRay = Ray.Zero();
